import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";

const TONE = { CREATE: "bg-emerald-100 text-emerald-900", LINK_EXISTING: "bg-sky-100 text-sky-900", BLOCKED: "bg-rose-100 text-rose-900", EXCLUDED: "bg-slate-200 text-slate-700", DONE: "bg-slate-100 text-slate-600" };
const VERB = { CREATE: "Create order", LINK_EXISTING: "Link existing order" };
const MODE = { ACCEPTED_EXISTING: "Accepted earlier — original acceptance kept", OWNER_CHAT: "SENT — owner confirmed in chat (3 Nov)", ACCEPTED_FUTURE: "Accepted after cutoff — order step failed", EXCLUDED: "Excluded on owner instruction" };

const List_ = ({ title, rows }) => (rows && rows.length ? <div><span className="font-semibold">{title}:</span> {rows.join(" · ")}</div> : null);

export function HandoverCard({ p, canDecide, reload }) {
  const [busy, setBusy] = useState(false);
  const [sure, setSure] = useState(false);
  const t = `oai-handover-${p.quote_id}`;
  const run = async (path, body) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/quote-acceptance/handover/${p.quote_id}/${path}`, body);
      toast.success(data.order_code ? `Order ${data.order_code} ready in Operations` : "Recorded");
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); reload(); }
    setBusy(false);
  };
  const o = p.orders || {};
  return (
    <div className="border-t border-slate-100 py-2 text-[12px]" data-testid={t}>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${TONE[p.plan] || ""}`} data-testid={`${t}-plan`}>{p.plan.replace("_", " ")}</span>
        <b className="font-mono">{p.code || p.label}</b>
        <span>{p.customer}</span>
        {p.status && <span className="text-slate-500">status {p.status}</span>}
        <span className="text-slate-500">{MODE[p.mode]}</span>
        <Link to={`/commercial/crm/quotations?open=${p.quote_id}`} className="underline" data-testid={`${t}-open`}>Open quotation</Link>
      </div>
      <div className="mt-1 space-y-0.5 text-slate-700">
        <List_ title="Cause" rows={p.cause} />
        <List_ title="Family" rows={(p.family || []).map((r) => `${r.code} ${r.status}`)} />
        <List_ title="Orders" rows={[...(o.canonical || []).map((x) => `${x.code} (Operations)`), ...(o.legacy || []).map((x) => `${x.code || x.order_number || x.id} (${x.collection})`)]} />
        {p.original_acceptance?.at && <div><span className="font-semibold">Original acceptance:</span> {p.original_acceptance.at.slice(0, 16).replace("T", " ")} UTC · {p.original_acceptance.channel || "—"} · {p.original_acceptance.by || "—"}</div>}
        {p.order_preview && <div><span className="font-semibold">Order from quotation:</span> {p.order_preview.lines} lines · {p.order_preview.currency} {p.order_preview.grand_total_inr ?? "—"} · PO {p.order_preview.po_number}</div>}
        {p.blockers?.map((b) => <div key={b} className="text-rose-700" data-testid={`${t}-blocker`}>Blocked: {b}</div>)}
        {p.warnings?.map((w) => <div key={w} className="text-amber-800">{w}</div>)}
        {p.repair?.state === "FAILED" && <div className="text-rose-700">Last attempt failed: {p.repair.error}</div>}
      </div>
      {canDecide && VERB[p.plan] && (
        <div className="mt-1 flex flex-wrap items-center gap-2">
          <label className="flex items-center gap-1"><input type="checkbox" checked={sure} onChange={(e) => setSure(e.target.checked)} data-testid={`${t}-confirm`} />I checked the dry run above</label>
          <button disabled={!sure || busy} onClick={() => run("apply", { expected_plan: p.plan, confirm: true })} className="rounded-full bg-emerald-700 px-3 py-0.5 font-semibold text-white disabled:bg-slate-300" data-testid={`${t}-apply`}>{busy ? "Applying…" : VERB[p.plan]}</button>
        </div>
      )}
      {canDecide && p.plan === "EXCLUDED" && !p.exclusion_recorded && <button disabled={busy} onClick={() => run("exclude")} className="mt-1 rounded-full border border-slate-300 px-3 py-0.5 font-semibold" data-testid={`${t}-exclude`}>Record exclusion</button>}
    </div>
  );
}
